import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { EventCategoryDto } from "@/api/types";
import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { FormCheckboxGroup } from "@/components/Form/FormCheckbox";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { getEventCategoryName } from "@/helpers/events";
import { useEventQueries } from "@/queries/events/queryOptions";
import { useTranslation } from "@/translations";

export type EventFilterValues = {
  createdBySelf?: boolean;
  signedUpBySelf?: boolean;
  category?: EventCategoryDto["id"];
};

type EventFilterDrawerProps = DrawerBaseProps & {
  filter: EventFilterValues;
  onUpdate: (values: EventFilterValues) => void;
};

export function EventFilterDrawer({
  isOpened,
  onOpenChange,
  filter,
  onUpdate,
}: EventFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<EventFilterValues>(filter);

  const { t } = useTranslation();
  const eventQueries = useEventQueries();
  const { data: eventCategories = [] } = useQuery(eventQueries.listCategories());

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    onUpdate({
      createdBySelf: false,
      signedUpBySelf: false,
      category: undefined,
    });
  };

  const eventTypeOptions = [
    {
      id: "all",
      name: t("model.event.category.all"),
    } as const,
    ...eventCategories,
  ];

  return (
    <Drawer.Root
      title={t("page.calendar.overview.filter-drawer.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
    >
      <Drawer.Body>
        <Stack gap="md">
          <FormField label={t("page.calendar.overview.filter-drawer.event-category.label")}>
            <Select
              placeholder={t("page.calendar.overview.filter-drawer.event-category.placeholder")}
              renderOption={(category) => getEventCategoryName(t, category.id)}
              keySelector={(category) => category.id}
              items={eventTypeOptions}
              selected={
                currFilter.category ? eventCategories.find((x) => x.id === currFilter.category) : eventTypeOptions[0]
              }
              onChange={(x) => setCurrFilter({ ...currFilter, category: x.id === "all" ? undefined : x.id })}
            />
          </FormField>
          <FormCheckboxGroup layout="vertical">
            <Checkbox
              label={t("page.calendar.overview.filter-drawer.signed-up-by-me.label")}
              checked={currFilter.signedUpBySelf}
              onChange={(e) => setCurrFilter({ ...currFilter, signedUpBySelf: e.target.checked })}
            />
            <Checkbox
              label={t("page.calendar.overview.filter-drawer.created-by-me.label")}
              checked={currFilter.createdBySelf}
              onChange={(e) => setCurrFilter({ ...currFilter, createdBySelf: e.target.checked })}
            />
          </FormCheckboxGroup>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
